/**
 * @fileoverview API client for Classification Rules feature.
 * @module features/classification-rules/api/classificationRules
 */

import apiClient from '@/lib/apiClient'
import {
  ClassificationRuleListSchema,
  ClassificationRuleRequestSchema,
  ClassificationRuleResponseSchema,
  type ClassificationRuleList,
  type ClassificationRuleRequest,
  type ClassificationRuleResponse,
} from '../schemas/classificationRules'

const API_BASE = '/classification/rules'

export async function listRules(): Promise<ClassificationRuleList> {
  const res = await apiClient.get(`${API_BASE}`)
  const parsed = ClassificationRuleListSchema.safeParse(res.data)
  if (!parsed.success) {
    // Developer diagnostics only; avoids leaking impl details to end users
    console.error(
      'classificationRules.listRules: Zod validation failed',
      parsed.error.issues,
      res.data
    )
    throw new Error('Invalid rules response')
  }
  return parsed.data
}

export async function getRule(id: number): Promise<ClassificationRuleResponse> {
  const res = await apiClient.get(`${API_BASE}/${id}`)
  return ClassificationRuleResponseSchema.parse(res.data)
}

export async function createRule(
  req: ClassificationRuleRequest
): Promise<ClassificationRuleResponse> {
  const body = ClassificationRuleRequestSchema.parse(req)
  const res = await apiClient.post(`${API_BASE}`, body)
  return ClassificationRuleResponseSchema.parse(res.data)
}

export async function updateRule(
  id: number,
  patch: Partial<ClassificationRuleRequest>
): Promise<ClassificationRuleResponse> {
  // Use PUT as per backend contract and tests (partial payload accepted by server)
  const body = ClassificationRuleRequestSchema.partial().parse(patch)
  const res = await apiClient.put(`${API_BASE}/${id}`, body)
  return ClassificationRuleResponseSchema.parse(res.data)
}

export async function deleteRule(id: number): Promise<void> {
  await apiClient.delete(`${API_BASE}/${id}`)
}

export default {
  listRules,
  getRule,
  createRule,
  updateRule,
  deleteRule,
}
